<template>
  <div class="auth-split-layout">
    <!-- 左: ブランドパネル -->
    <div class="auth-split-left">
      <div class="auth-brand">
        <img src="/favicon.png" alt="" />
        <span class="auth-brand-name">RabiiTrip</span>
      </div>
      <h1>旅のすべてを、みんなの共同作業に。</h1>
      <p class="auth-tagline">
        行き先候補の比較から旅のしおり作成、スケジュール、持ち物、割り勘まで。旅行のすべてをひとつのアプリで。
      </p>
    </div>

    <!-- 右: フォームカード -->
    <div class="auth-split-right">
      <div class="auth-form-card">
        <h2 class="auth-form-title">{{ getTitle }}</h2>

        <!-- タブ -->
        <div class="auth-tabs" role="tablist">
          <button
            :class="['auth-tab', { active: activeTab === 'login' }]"
            @click="activeTab = 'login'"
            role="tab"
            :aria-selected="activeTab === 'login'"
            id="tab-login"
            aria-controls="panel-login"
          >ログイン</button>
          <button
            :class="['auth-tab', { active: activeTab === 'register' }]"
            @click="activeTab = 'register'"
            role="tab"
            :aria-selected="activeTab === 'register'"
            id="tab-register"
            aria-controls="panel-register"
          >新規登録</button>
          <button
            :class="['auth-tab', { active: activeTab === 'reset' }]"
            @click="activeTab = 'reset'"
            role="tab"
            :aria-selected="activeTab === 'reset'"
            id="tab-reset"
            aria-controls="panel-reset"
          >パスワード再設定</button>
        </div>

        <!-- ログインフォーム -->
        <form
          v-if="activeTab === 'login'"
          @submit.prevent="handleLogin"
          class="auth-form"
          id="panel-login"
          role="tabpanel"
          aria-labelledby="tab-login"
        >
          <div class="form-group">
            <label class="form-label" for="login-email">メールアドレス</label>
            <input
              id="login-email"
              v-model="email"
              type="email"
              placeholder="example@email.com"
              class="form-input"
              autocomplete="email"
              required
            />
          </div>
          <div class="form-group">
            <label class="form-label" for="login-password">パスワード</label>
            <input
              id="login-password"
              v-model="password"
              type="password"
              placeholder="パスワード"
              class="form-input"
              autocomplete="current-password"
              required
            />
          </div>
          <button type="submit" class="btn btn-primary btn-auth" :disabled="isLoading">
            <span v-if="!isLoading">ログイン</span>
            <span v-else class="btn-loading"><span class="spinner"></span>ログイン中...</span>
          </button>
          <div class="register-switch">
            <span>アカウントをお持ちでない方は</span>
            <button type="button" @click="activeTab = 'register'">
              こちらから新規登録
            </button>
          </div>
          <div v-if="error" class="alert alert-error" role="alert">{{ error }}</div>
          <div v-if="successMessage" class="alert alert-success" role="status">{{ successMessage }}</div>
        </form>

        <!-- 新規登録フォーム -->
        <form
          v-if="activeTab === 'register'"
          @submit.prevent="handleRegister"
          class="auth-form"
          id="panel-register"
          role="tabpanel"
          aria-labelledby="tab-register"
        >
          <div class="form-group">
            <label class="form-label" for="reg-username">ユーザー名</label>
            <input id="reg-username" v-model="registerUserName" type="text" placeholder="山田太郎" class="form-input" autocomplete="username" required />
          </div>
          <div class="form-group">
            <label class="form-label" for="reg-email">メールアドレス</label>
            <input id="reg-email" v-model="registerEmail" type="email" placeholder="example@email.com" class="form-input" autocomplete="email" required />
          </div>
          <div class="form-group">
            <label class="form-label" for="reg-password">パスワード</label>
            <input id="reg-password" v-model="registerPassword" type="password" placeholder="8文字以上" class="form-input" autocomplete="new-password" minlength="8" required />
          </div>
          <div class="form-group">
            <label class="form-label" for="reg-password-confirm">パスワード（確認）</label>
            <input id="reg-password-confirm" v-model="registerPasswordConfirm" type="password" placeholder="パスワードを再入力" class="form-input" autocomplete="new-password" required />
          </div>
          <button type="submit" class="btn btn-primary btn-auth" :disabled="isLoading">
            <span v-if="!isLoading">新規登録</span>
            <span v-else class="btn-loading"><span class="spinner"></span>登録中...</span>
          </button>
          <div v-if="error" class="alert alert-error" role="alert">{{ error }}</div>
          <div v-if="successMessage" class="alert alert-success" role="status">{{ successMessage }}</div>
        </form>

        <!-- パスワードリセットフォーム -->
        <form
          v-if="activeTab === 'reset'"
          @submit.prevent="handlePasswordReset"
          class="auth-form"
          id="panel-reset"
          role="tabpanel"
          aria-labelledby="tab-reset"
        >
          <p class="form-description">
            登録されているメールアドレスにパスワード再設定用のリンクを送信します。
          </p>
          <div class="form-group">
            <label class="form-label" for="reset-email">メールアドレス</label>
            <input id="reset-email" v-model="resetEmail" type="email" placeholder="example@email.com" class="form-input" autocomplete="email" required />
          </div>
          <button type="submit" class="btn btn-primary btn-auth" :disabled="isLoading">
            <span v-if="!isLoading">再設定メールを送信</span>
            <span v-else class="btn-loading"><span class="spinner"></span>送信中...</span>
          </button>
          <div v-if="error" class="alert alert-error" role="alert">{{ error }}</div>
          <div v-if="successMessage" class="alert alert-success" role="status">{{ successMessage }}</div>
        </form>

        <!-- Google認証 -->
        <div class="social-auth">
          <div class="auth-divider"><span>または</span></div>
          <button type="button" @click="handleGoogleLogin" class="btn-google" :disabled="isLoading">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Googleでログイン
          </button>
        </div>

        <div class="auth-back-link">
          <router-link to="/">← トップページに戻る</router-link>
        </div>
      </div>
    </div>

    <!-- アカウント復元モーダル -->
    <div
      v-if="showRestoreModal"
      class="modal-overlay"
      @click.self="closeRestoreModal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="restore-modal-title"
    >
      <div class="modal-content restore-modal">
        <h3 id="restore-modal-title">アカウントの復元</h3>
        <p>
          このアカウントは削除済みです。<br />
          削除日: {{ restoreInfo.deleteDate }}<br />
          残り{{ restoreInfo.daysRemaining }}日間は復元できます。
        </p>
        <p class="restore-question">アカウントを復元しますか？</p>
        <div class="modal-actions">
          <button @click="handleRestore" class="btn btn-primary" :disabled="isRestoring">
            {{ isRestoring ? "復元中..." : "復元する" }}
          </button>
          <button @click="cancelRestore" class="btn btn-secondary">
            復元しない（ログアウト）
          </button>
        </div>
        <p v-if="restoreError" class="alert alert-error" role="alert">{{ restoreError }}</p>
      </div>
    </div>
  </div>

</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "../store/authStore.js";
import axios from "axios";
import { API_URL } from "../config/api.js";

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

// タブ管理
const activeTab = ref("login");

// 復元モーダル管理
const showRestoreModal = ref(false);
const restoreInfo = ref({
  deleteDate: "",
  daysRemaining: 0,
});
const isRestoring = ref(false);
const restoreError = ref("");

// ログイン用
const email = ref("");
const password = ref("");

// 新規登録用
const registerUserName = ref("");
const registerEmail = ref("");
const registerPassword = ref("");
const registerPasswordConfirm = ref("");

// パスワードリセット用
const resetEmail = ref("");

// 状態管理
const isLoading = ref(false);
const error = ref(null);
const successMessage = ref(null);

// Google認証エラーの確認
onMounted(() => {
  const errorParam = route.query.error;
  const detailParam = route.query.detail;
  const messageParam = route.query.message;

  if (errorParam) {
    const errorMessages = {
      google_auth_failed: "Google認証に失敗しました。もう一度お試しください。",
      no_code: "認証コードが取得できませんでした。",
      token_error: "Googleトークンの取得に失敗しました。",
      no_email: "Googleアカウントからメールアドレスを取得できませんでした。",
      http_error: "Google APIとの通信でエラーが発生しました。",
      account_deleted: messageParam || "このアカウントは削除されています。",
    };

    let errorMsg =
      errorMessages[errorParam] || "Google認証でエラーが発生しました。";
    if (detailParam) {
      errorMsg += ` (${detailParam})`;
    }

    error.value = errorMsg;
    console.error("Google OAuth error:", errorParam, detailParam);

    // エラーパラメータをクリア
    router.replace({ query: {} });
  }
});

// タイトル
const getTitle = computed(() => {
  switch (activeTab.value) {
    case "login":
      return "ログイン";
    case "register":
      return "新規登録";
    case "reset":
      return "パスワード再設定";
    default:
      return "ログイン";
  }
});

// ログイン処理
const handleLogin = async () => {
  try {
    isLoading.value = true;
    error.value = null;
    successMessage.value = null;

    const response = await authStore.login(email.value, password.value);

    // 削除済みアカウントの場合
    if (response && response.deleted_account) {
      restoreInfo.value = {
        deleteDate: response.delete_date,
        daysRemaining: response.days_remaining,
      };
      showRestoreModal.value = true;
      isLoading.value = false;
      return;
    }

    if (authStore.token) {
      const redirect = route.query.redirect;
      router.push(redirect || "/mypage");
    } else {
      error.value = authStore.error || "ログインに失敗しました";
    }
  } catch (err) {
    console.error("ログインエラー:", err);
    error.value =
      "ログインに失敗しました。メールアドレスとパスワードを確認してください。";
  } finally {
    isLoading.value = false;
  }
};

// アカウント復元処理
const handleRestore = async () => {
  isRestoring.value = true;
  restoreError.value = "";

  try {
    const token = localStorage.getItem("token");
    await axios.post(
      `${API_URL}/users/restore`,
      {},
      { headers: { Authorization: `Bearer ${token}` } },
    );

    // 復元成功後、ユーザー情報を再取得してマイページへ
    await authStore.fetchUser();
    showRestoreModal.value = false;
    router.push("/mypage");
  } catch (error) {
    console.error("復元エラー:", error);
    restoreError.value = "アカウントの復元に失敗しました";
  } finally {
    isRestoring.value = false;
  }
};

// 復元をキャンセル（ログアウト）
const cancelRestore = () => {
  authStore.logout();
  showRestoreModal.value = false;
};

// モーダルを閉じる
const closeRestoreModal = () => {
  showRestoreModal.value = false;
  authStore.logout();
};

// 新規登録処理
const handleRegister = async () => {
  try {
    isLoading.value = true;
    error.value = null;
    successMessage.value = null;

    // ユーザー名チェック
    if (!registerUserName.value || registerUserName.value.trim() === "") {
      error.value = "ユーザー名を入力してください";
      isLoading.value = false;
      return;
    }

    // パスワード一致確認
    if (registerPassword.value !== registerPasswordConfirm.value) {
      error.value = "パスワードが一致しません";
      isLoading.value = false;
      return;
    }

    // パスワード強度チェック
    if (registerPassword.value.length < 8) {
      error.value = "パスワードは8文字以上にしてください";
      isLoading.value = false;
      return;
    }

    const response = await authStore.register(
      registerEmail.value,
      registerPassword.value,
      registerUserName.value,
    );

    // 削除済みアカウントの場合、復元モーダルを表示
    if (response && response.deleted_account) {
      restoreInfo.value = {
        deleteDate: response.delete_date,
        daysRemaining: response.days_remaining,
      };
      showRestoreModal.value = true;
      isLoading.value = false;
      return;
    }

    if (authStore.token) {
      const redirect = route.query.redirect;
      successMessage.value = redirect
        ? "登録が完了しました！共有しおりへ移動します..."
        : "登録が完了しました！マイページへ移動します...";
      setTimeout(() => {
        router.push(redirect || "/mypage");
      }, 1500);
    } else {
      error.value = authStore.error || "登録に失敗しました";
    }
  } catch (err) {
    console.error("登録エラー:", err);
    error.value =
      err.response?.data?.detail ||
      "登録に失敗しました。もう一度お試しください。";
  } finally {
    isLoading.value = false;
  }
};

// パスワードリセット処理
const handlePasswordReset = async () => {
  try {
    isLoading.value = true;
    error.value = null;
    successMessage.value = null;

    await authStore.requestPasswordReset(resetEmail.value);

    successMessage.value =
      "パスワード再設定用のメールを送信しました。メールをご確認ください。";
    resetEmail.value = "";
  } catch (err) {
    console.error("パスワードリセットエラー:", err);
    error.value =
      "メール送信に失敗しました。メールアドレスを確認してください。";
  } finally {
    isLoading.value = false;
  }
};

// Google認証処理
const handleGoogleLogin = () => {
  // Google認証画面にリダイレクト（エラーハンドリング不要）
  window.location.href = `${API_URL}/auth/google`;
};
</script>

<style scoped>
/* ── フォームカード ── */
.auth-form-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  padding: 2rem;
  width: 100%;
  max-width: 440px;
}

.auth-form-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 1.25rem;
  text-align: center;
}

/* ── タブ ── */
.auth-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 1.5rem;
  background: var(--color-background);
  border-radius: var(--radius-md);
  padding: 3px;
}

.auth-tab {
  flex: 1;
  padding: 0.5rem 0.25rem;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.auth-tab.active {
  background: var(--color-surface);
  color: var(--color-text);
  box-shadow: var(--shadow-sm);
}

/* ── フォーム ── */
.auth-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0;
}

.form-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 1rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary-ink);
  box-shadow: 0 0 0 3px var(--color-primary-light);
}

.form-description {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  line-height: 1.6;
  margin-bottom: 0;
}

.btn-auth {
  width: 100%;
  padding: 0.75rem;
  font-size: 0.9375rem;
  margin-top: 0.5rem;
  border-radius: var(--radius-md);
  min-height: 44px;
}

.btn-loading {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.register-switch {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  line-height: 1.5;
  text-align: center;
}

.register-switch button {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--color-primary-ink);
  cursor: pointer;
  font: inherit;
  font-weight: 700;
}

.register-switch button:hover {
  text-decoration: underline;
}

/* ── ソーシャル認証 ── */
.social-auth {
  margin-top: 1rem;
}

.auth-divider {
  position: relative;
  text-align: center;
  margin: 1rem 0;
}

.auth-divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--color-border);
}

.auth-divider span {
  position: relative;
  background: var(--color-surface);
  padding: 0 0.75rem;
  font-size: 0.8125rem;
  color: var(--color-text-muted);
}

.btn-google {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  padding: 0.6875rem 1rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text);
  cursor: pointer;
  transition: background var(--transition-fast), box-shadow var(--transition-fast);
  min-height: 44px;
}

.btn-google:hover:not(:disabled) {
  background: var(--color-background);
  box-shadow: var(--shadow-sm);
}

.btn-google:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── 戻るリンク ── */
.auth-back-link {
  text-align: center;
  margin-top: 1.25rem;
}

.auth-back-link a {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  text-decoration: none;
  transition: color var(--transition-fast);
}

.auth-back-link a:hover {
  color: var(--color-text);
}

/* ── 復元モーダル ── */
.restore-modal {
  border-radius: var(--radius-xl);
  padding: 2rem;
  max-width: 440px;
}

.restore-modal h3 {
  font-size: 1.125rem;
  color: var(--color-text);
  margin-bottom: 1rem;
}

.restore-modal p {
  color: var(--color-text-muted);
  margin-bottom: 0.5rem;
  line-height: 1.6;
}

.restore-question {
  font-weight: 600;
  color: var(--color-text) !important;
  margin-bottom: 1.25rem !important;
}

.modal-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}
</style>
